import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

interface PlayerStats {
  nama: string;
  rank: string;
  poin: number;
  winRate: number;
  total: number;
  wins: number;
  losses: number;
  previousPoints: string;
  winLossHistory: string;
  opponents: string;
  level: string;
  highestRank?: string;
  highestPoints?: number;
  overallRank?: number;
  levelRank?: number;
}



const RankNumber: React.FC<{ position: number }> = ({ position }) => {
  if (position <= 3) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: position === 1 ? '#FFD700' : position === 2 ? '#C0C0C0' : '#CD7F32',
        color: 'white',
        fontWeight: 700,
        fontSize: '1.1rem',
      }}>
        {position}
      </Box>
    );
  }
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 40,
      height: 40,
      borderRadius: '50%',
      background: 'rgba(0,0,0,0.1)',
      color: 'text.primary',
      fontWeight: 600,
    }}>
      {position}
    </Box>
  );
};

const getMaskedName = (name: string, winRate: number): string => {
  if (winRate < 50) {
    return name.replace(/./g, (char, index) => {
      if (index === 0 || index === name.length - 1) return char;
      return '*';
    });
  }
  return name;
};

interface PlayerStatsModalProps {
  open: boolean;
  onClose: () => void;
  player: PlayerStats | null;
}

const PlayerStatsModal: React.FC<PlayerStatsModalProps> = ({ open, onClose, player }) => {
  const theme = useTheme();

  if (!player) return null;

  const parseHistory = (history: string, count: number) => {
    if (!history) return Array(count).fill(null);
    return history.split(',').slice(0, count).map(item => {
      const trimmed = item.trim().toUpperCase();
      return trimmed === 'W';
    });
  };

  const winLossData = parseHistory(player.winLossHistory, 10);

  const reversedWinLossData = [...winLossData].reverse();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.98) 100%)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255,255,255,0.1)'
            : '1px solid rgba(0,0,0,0.1)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        color: 'text.primary',
        borderBottom: theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(0,0,0,0.1)'
      }}>
        <TrophyIcon />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {getMaskedName(player.nama, player.winRate)}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Ranked Nova
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onClose} sx={{ color: 'text.primary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
                : 'rgba(0,0,0,0.02)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(99,170,255,0.22)'
                : '1px solid rgba(0,0,0,0.1)',
              height: 'fit-content',
              borderRadius: 3,
            }}>
              <CardContent>
                <Typography variant="h6" sx={{
                  color: 'text.primary',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrophyIcon />
                  Statistik Dasar
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Ranking:
                    </Typography>
                    <Chip
                      label={player.rank}
                      sx={{
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Poin:
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {player.poin}
                    </Typography>
                  </Box>

                  {player.overallRank && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Rank All Level:
                      </Typography>
                      <Chip
                        label={`#${player.overallRank}`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  )}

                  {player.levelRank && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Rank pada Level {player.level}:
                      </Typography>
                      <Chip
                        label={`#${player.levelRank}`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  )}

                  {player.highestRank && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Rank Tertinggi:
                      </Typography>
                      <Chip
                        label={player.highestRank}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  )}

                  {player.highestPoints && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Poin Tertinggi:
                      </Typography>
                      <Typography variant="body1" sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}>
                        {player.highestPoints}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Win Rate:
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: player.winRate >= 50 ? '#4CAF50' : '#F44336',
                      fontWeight: 600
                    }}>
                      {player.winRate}%
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Total Match:
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {player.total}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Menang:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                      {player.wins}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Kalah:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#F44336', fontWeight: 600 }}>
                      {player.losses}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
                : 'rgba(0,0,0,0.02)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(99,170,255,0.22)'
                : '1px solid rgba(0,0,0,0.1)',
              height: 'fit-content',
              borderRadius: 3,
            }}>
              <CardContent>
                <Typography variant="h6" sx={{
                  color: 'text.primary',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrendingUpIcon />
                  10 Pertandingan Terakhir
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                  {reversedWinLossData.map((isWin, index) => (
                    <Box key={index} sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <Chip
                        label={isWin ? 'W' : 'L'}
                        sx={{
                          background: isWin
                            ? 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)'
                            : 'linear-gradient(45deg, #F44336 30%, #EF5350 90%)',
                          color: 'white',
                          fontWeight: 600,
                          minHeight: '36px',
                          fontSize: '1rem',
                          '& .MuiChip-label': {
                            px: 2,
                            py: 1
                          }
                        }}
                      />
                    </Box>
                  ))}
                  {reversedWinLossData.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Belum ada data pertandingan
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LocalRankedMatch: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = theme.palette.mode;

  const [rankedData, setRankedData] = useState<PlayerStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('Gabungan');
  const [isMobileView, setIsMobileView] = useState(true);
  const [playerStatsModalOpen, setPlayerStatsModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerStats | null>(null);

  // Fetch ranked nova data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/bookings?action=ranked-leaderboard-local');
        if (response.data && response.data.ranked_nova) {
          setRankedData(Array.isArray(response.data.ranked_nova) ? response.data.ranked_nova : []);
        }
      } catch (error) {
        console.error('Error fetching ranked nova data:', error);
        setRankedData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle mobile view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 900) {
        setIsMobileView(false);
      } else {
        setIsMobileView(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle player click
  const handlePlayerClick = (player: PlayerStats) => {
    setSelectedPlayer(player);
    setPlayerStatsModalOpen(true);
    const urlName = player.nama.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    navigate(`/local-ranked-match/${urlName}`, { replace: true });
  };

  // Close modal
  const handleCloseModal = () => {
    setPlayerStatsModalOpen(false);
    setSelectedPlayer(null);
    navigate('/local-ranked-match', { replace: true });
  };

  // Filter data
  const getFilteredData = () => {
    let filtered = rankedData;

    // Filter by level
    if (selectedLevel !== 'Gabungan') {
      filtered = filtered.filter((player: PlayerStats) => {
        const playerLevel = String(player.level || '').toLowerCase();
        const selected = String(selectedLevel || '').toLowerCase();

        let canonical: 'beginner' | 'intermediate' | 'advanced' | null = null;
        if (selected.includes('beginner') || selected.includes('pemula')) canonical = 'beginner';
        else if (selected.includes('intermediate') || selected.includes('menengah')) canonical = 'intermediate';
        else if (selected.includes('advanced') || selected.includes('lanjutan') || selected.includes('mahir')) canonical = 'advanced';

        if (!canonical) return false;

        if (canonical === 'beginner') {
          return playerLevel.includes('beginner') || playerLevel.includes('pemula');
        }
        if (canonical === 'intermediate') {
          return playerLevel.includes('intermediate') || playerLevel.includes('menengah');
        }
        return playerLevel.includes('advanced') || playerLevel.includes('lanjutan') || playerLevel.includes('mahir');
      });
    }

    // Filter by search
    if (searchQuery.trim()) {
      filtered = filtered.filter((player: PlayerStats) => {
        const playerName = (player.nama || '').toLowerCase();
        const query = searchQuery.toLowerCase().trim();

        if (playerName.includes(query)) {
          if (player.winRate >= 50) {
            return true;
          }
        }
        return false;
      });
    }

    return filtered.sort((a: PlayerStats, b: PlayerStats) => b.poin - a.poin);
  };

  const filteredData = getFilteredData();

  return (
    <>
      <Helmet>
        <title>Local Ranked Match - RaketerS</title>
        <meta name="description" content="Local Ranked Match Leaderboard" />
      </Helmet>

      <Box sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
          🏆 Local Ranked Match
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
          Leaderboard pemain Ranked Match Regional Sumedang
        </Typography>

        {/* Controls */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr' },
          gap: 2,
          mb: 3
        }}>
          <TextField
            placeholder="Cari nama pemain..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Level</InputLabel>
            <Select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              label="Level"
            >
              <MenuItem value="Gabungan">Gabungan</MenuItem>
              <MenuItem value="Beginner">Beginner</MenuItem>
              <MenuItem value="Intermediate">Intermediate</MenuItem>
              <MenuItem value="Advanced">Advanced</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* No data */}
        {!loading && filteredData.length === 0 && (
          <Alert severity="info">
            Tidak ada data pemain yang sesuai dengan filter Anda
          </Alert>
        )}

        {/* Table View */}
        {!loading && !isMobileView && filteredData.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ background: mode === 'dark' ? 'rgba(30,30,30,0.5)' : 'rgba(0,0,0,0.04)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nama Pemain</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Level</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Poin</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Win Rate</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Match</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((player, index) => (
                  <TableRow key={index} hover sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <RankNumber position={index + 1} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getMaskedName(player.nama, player.winRate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={player.level || '-'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {player.poin}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{
                        color: player.winRate >= 50 ? '#4CAF50' : '#F44336',
                        fontWeight: 600
                      }}>
                        {player.winRate}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {player.wins}W - {player.losses}L
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handlePlayerClick(player)}
                        sx={{
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      >
                        Lihat Detail
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Card View */}
        {!loading && isMobileView && filteredData.length > 0 && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2
          }}>
            {filteredData.map((player, index) => (
              <Card
                key={index}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  },
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(30,30,30,0.9) 0%, rgba(40,40,40,0.95) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.98) 100%)',
                }}
                onClick={() => handlePlayerClick(player)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                    <RankNumber position={index + 1} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {getMaskedName(player.nama, player.winRate)}
                      </Typography>
                      <Chip label={player.level || '-'} size="small" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Poin
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {player.poin}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Win Rate
                      </Typography>
                      <Typography variant="body1" sx={{
                        fontWeight: 600,
                        color: player.winRate >= 50 ? '#4CAF50' : '#F44336'
                      }}>
                        {player.winRate}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Total Match
                      </Typography>
                      <Typography variant="body2">
                        {player.total}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Record
                      </Typography>
                      <Typography variant="body2">
                        {player.wins}W - {player.losses}L
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    sx={{
                      mt: 2,
                      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  >
                    Lihat Detail
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Player Stats Modal */}
      <PlayerStatsModal
        open={playerStatsModalOpen}
        onClose={handleCloseModal}
        player={selectedPlayer}
      />
    </>
  );
};

export default LocalRankedMatch;
